<script setup lang="ts">
import { EyeHiddenIcon, EyeIcon } from '@/assets'
import { ipLikeRegex, numericRegexWithPrecision } from '@/utils/utils'
import { computed, ref, useId, useTemplateRef, type Component } from 'vue'

const props = withDefaults(
  defineProps<{
    name?: string
    id?: string
    type?: 'text' | 'password' | 'num' | 'ip' | 'email'
    disablePasswordPreview?: boolean
    precision?: number
    allowNegative?: boolean
    allowMultipleZeros?: boolean
    label?: string
    unit?: string
    icon?: Component
    error?: boolean
    disabled?: boolean
    autocomplete?: string
    required?: boolean
    placeholder?: string
    transparent?: boolean
  }>(),
  {
    type: 'text',
    password: false,
    disablePasswordPreview: false,
    precision: undefined,
    allowNegative: true,
    allowMultipleZeros: false,
    label: undefined,
    unit: undefined,
    icon: undefined,
  },
)

const model = defineModel<string>()

const id = props.id || useId()
const input = useTemplateRef<HTMLInputElement>('inputRef')
const passwordOverride = ref(false)

function getInputRef() {
  return input
}

function numericGuard(event: InputEvent) {
  const target = event.target as HTMLInputElement
  const inputType = event.inputType
  const data = event.data || ''

  // Allow deletion operations
  if (inputType.startsWith('delete')) {
    return
  }

  // For insertions, validate the character
  if (inputType === 'insertText' || inputType === 'insertFromPaste') {
    const currentValue = target.value
    const selectionStart = target.selectionStart || 0
    const selectionEnd = target.selectionEnd || 0

    // Simulate what the new value would be
    const newValue = currentValue.slice(0, selectionStart) + data + currentValue.slice(selectionEnd)
    const isValidPartialNumber = numericRegexWithPrecision(props.precision, props.allowNegative, props.allowMultipleZeros).test(newValue)

    if (!isValidPartialNumber) {
      event.preventDefault()
      return
    }
  }
}

function ipGuard(event: InputEvent) {
  const target = event.target as HTMLInputElement
  const inputType = event.inputType
  const data = event.data || ''

  // Allow deletion operations
  if (inputType.startsWith('delete')) {
    return
  }

  // For insertions, validate the character
  if (inputType === 'insertText' || inputType === 'insertFromPaste') {
    const currentValue = target.value
    const selectionStart = target.selectionStart || 0
    const selectionEnd = target.selectionEnd || 0

    // Simulate what the new value would be
    const newValue = currentValue.slice(0, selectionStart) + data + currentValue.slice(selectionEnd)
    const isValidPartialNumber = ipLikeRegex.test(newValue)

    if (!isValidPartialNumber) {
      event.preventDefault()
      return
    }
  }
}

function inputGuard(event: InputEvent) {
  switch (props.type) {
    case 'num':
      numericGuard(event)
      break
    case 'ip':
      ipGuard(event)
      break
    // Add more cases for different types if needed
  }
}

const inputMode = computed(() => {
  switch (props.type) {
    case 'num':
      return props.precision !== undefined && props.precision !== 0 ? 'decimal' : 'numeric'
    case 'ip':
      return 'numeric'
    default:
      return 'text'
  }
})

defineExpose({
  getInputRef,
})

defineEmits<{
  blur: [payload: FocusEvent]
}>()
</script>

<template>
  <div class="wrapper">
    <label :for="id" class="label">
      <template v-if="label">
        {{ label }}
      </template>
      <template v-else>
        <slot></slot>
      </template>
    </label>
    <div class="input-wrapper" :class="{ error: error, disabled: disabled, transparent: transparent }">
      <input
        v-model="model"
        :id="id"
        :name="name"
        ref="inputRef"
        class="input-box"
        :class="{ error: error }"
        :type="type === 'password' && !passwordOverride ? 'password' : type === 'email' ? 'email' : 'text'"
        :inputmode="inputMode"
        :autocomplete="autocomplete"
        :required="required"
        :disabled="disabled"
        :placeholder="placeholder"
        @beforeinput="inputGuard"
        @blur="$emit('blur', $event)"
      />
      <div v-if="unit || icon" class="input-suffix">
        <span v-if="unit" class="unit">
          {{ unit }}
        </span>
        <component :is="icon" v-if="icon" />
      </div>
      <button
        type="button"
        v-if="type === 'password' && !disablePasswordPreview"
        class="preview-button"
        @click="passwordOverride = !passwordOverride"
      >
        <EyeIcon v-if="!passwordOverride" />
        <EyeHiddenIcon v-else />
      </button>
    </div>
    <span v-if="$slots.error && error" class="error-message">
      <slot name="error"></slot>
    </span>
  </div>
</template>

<style lang="scss" scoped>
.wrapper {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.label {
  font-size: var(--ullib-size-font-medium);
  line-height: var(--ullib-size-line-spacing-medium);
  font-weight: 700;
  margin-bottom: 4px;
  color: var(--ullib-color-accent-1);
}

.input-wrapper {
  width: 100%;
  height: 40px;
  display: flex;
  background-color: var(--ullib-color-contrast);
  border: 1px solid var(--ullib-color-accent-2);
  border-radius: 4px;
  flex-direction: row;
  min-width: 0;
  position: relative;
  flex-grow: 1;
  overflow: hidden;

  &:focus-within {
    box-shadow: 0px 0px 8px var(--ullib-color-shadow);
  }

  &.error {
    border-color: var(--ullib-color-red);
  }

  &.disabled {
    background-color: var(--ullib-color-disabled-input);
    border-color: var(--ullib-color-accent-2);
    .input-box {
      &::placeholder {
        opacity: 30%;
      }
    }
  }

  &.transparent {
    background-color: transparent;
    &.disabled {
      background-color: var(--ullib-color-disabled-input-transparent);
    }
  }
}

.input-box {
  flex-grow: 1;
  flex-shrink: 1;
  border: none;
  padding: 0 12px;
  display: flex;
  align-items: center;
  min-width: 0;
  background-color: transparent;

  &::placeholder {
    color: var(--ullib-color-accent-1);
  }
}

.input-box[type='password']:not(:placeholder-shown) {
  letter-spacing: -4px;
  font-family: 'ULPasswordDots';
  font-style: normal;
  font-weight: normal;
  font-variant: normal;
  text-transform: none;
}

.input-box:focus {
  outline: none;
}

.input-suffix {
  padding-right: 12px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--ullib-color-primary);
  overflow: visible;
  pointer-events: none;
}

.unit {
  font-weight: 500;
  color: var(--ullib-color-accent-1);
  > * {
    flex-shrink: 0;
  }
}

.preview-button {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 42px;
  height: 100%;
  border: none;
  background-color: transparent;
  padding: 0;
  cursor: pointer;
}

.preview-button:active {
  outline: none;
}

.error-message {
  margin-top: 4px;
  font-size: var(--ullib-size-font-small);
  line-height: var(--ullib-size-line-spacing-small);
  font-weight: 500;
  color: var(--ullib-color-red);
}
</style>
